if (window.jdgmSettings) {
  window.jdgmSettings.enable_review_pictures = true;
  window.jdgmSettings.enable_review_videos = true;
  window.jdgmSettings.show_review_title_input = true;
  window.jdgmSettings.widget_show_review_title_input = true;
  window.jdgmSettings.review_form_theme = 'compact';
  window.jdgmSettings.widget_show_photo_gallery = true;
  window.jdgmSettings.review_widget_expanded_media_gallery_enabled = false;
  window.jdgmSettings.review_widget_image_style = 'thumbnails';
  window.jdgmSettings.review_widget_header_theme = 'standard';
  window.jdgmSettings.review_widget_average_rating_style = 'minimal';
  window.jdgmSettings.review_widget_bar_chart_type = 'stars';
}

if (!customElements.get('theme-judgeme-reviews')) {
  customElements.define(
    'theme-judgeme-reviews',
    class ThemeJudgemeReviews extends HTMLElement {
      connectedCallback() {
        this.enhanceQueued = false;
        this.mediaByTrigger = new WeakMap();
        this.onClick = this.onClick.bind(this);
        this.onMutations = this.onMutations.bind(this);
        this.onBodyMutations = this.onBodyMutations.bind(this);

        this.addEventListener('click', this.onClick, true);
        this.observer = new MutationObserver(this.onMutations);
        this.observer.observe(this, { childList: true, subtree: true });
        this.bodyObserver = new MutationObserver(this.onBodyMutations);
        this.bodyObserver.observe(document.body, { childList: true, subtree: true });
        this.queueEnhance();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick, true);
        this.observer?.disconnect();
        this.bodyObserver?.disconnect();
        this.closeMediaViewer();
      }

      onMutations() {
        this.queueEnhance();
      }

      onBodyMutations(mutations) {
        const hasReviewForm = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              (node.matches?.('.jdgm-write-review-modal, .jdgm-modal, .jdgm-modal-block') ||
                node.querySelector?.('.jdgm-write-review-modal, .jdgm-modal, .jdgm-modal-block'))
          )
        );

        if (hasReviewForm) {
          requestAnimationFrame(() => {
            this.enhanceNativeReviewFormStyle();
            this.enhanceMediaFields();
          });
        }
      }

      queueEnhance() {
        if (this.enhanceQueued) return;

        this.enhanceQueued = true;
        requestAnimationFrame(() => {
          this.enhanceQueued = false;
          this.enhanceWidget();
        });
      }

      enhanceWidget() {
        this.enhanceSummary();
        this.enhanceV3Summary();
        this.enhanceSortDropdown();
        this.enhanceFilterDropdown();
        this.enhanceNativeReviewFormStyle();
        this.enhanceMediaFields();
        this.enhanceMediaGallery();
        this.enhanceV3Widget();
      }

      enhanceV3Widget() {
        this.querySelector('.jm-review-widget')?.classList.add('theme-judgeme-v3');
      }

      enhanceV3Summary() {
        const summary = this.querySelector('.jm-review-widget-header__average-rating-display');
        if (!summary) return;

        const reviewCount = this.getReviewCount();
        const averageRating = this.getAverageRating(summary);
        if (reviewCount <= 0 || averageRating <= 0) return;

        let customSummary = summary.querySelector('.theme-judgeme-v3-summary');
        if (!customSummary) {
          const starMarkup = Array.from(
            { length: 5 },
            () => '<span class="jdgm-star jdgm--on"></span>'
          ).join('');

          customSummary = document.createElement('div');
          customSummary.className = 'theme-judgeme-v3-summary';
          customSummary.innerHTML = `
            <div class="theme-judgeme-v3-summary__rating-row">
              <span class="theme-judgeme-v3-summary__stars jdgm-prev-badge__stars" aria-hidden="true">
                ${starMarkup}
              </span>
              <span class="theme-judgeme-v3-summary__score"></span>
            </div>
            <p class="theme-judgeme-v3-summary__count"></p>
          `;
          summary.append(customSummary);
        }

        summary.classList.add('is-theme-enhanced');
        this.setText(
          customSummary.querySelector('.theme-judgeme-v3-summary__score'),
          `${this.formatRating(averageRating)} out of 5`
        );
        this.setText(
          customSummary.querySelector('.theme-judgeme-v3-summary__count'),
          `Based on ${reviewCount.toLocaleString()} ${reviewCount === 1 ? 'review' : 'reviews'}`
        );
      }

      enhanceSummary() {
        const summary = this.querySelector('.jdgm-rev-widg__summary');
        if (!summary) return;

        const summaryInner = summary.querySelector('.jdgm-rev-widg__summary-inner') || summary;
        const existingScore = summaryInner.querySelector('.theme-judgeme-summary__score');
        const averageRating = this.getAverageRating(summary);

        if (!existingScore && averageRating > 0) {
          const score = document.createElement('span');
          score.className = 'theme-judgeme-summary__score';
          score.textContent = `${this.formatRating(averageRating)} out of 5`;

          const stars =
            summaryInner.querySelector('.jdgm-rev-widg__summary-stars') ||
            summaryInner.querySelector('[data-score]') ||
            summaryInner.querySelector('.jdgm-star')?.parentElement;

          if (stars) {
            stars.insertAdjacentElement('afterend', score);
          } else {
            summaryInner.prepend(score);
          }
        }

        const summaryText = summaryInner.querySelector('.jdgm-rev-widg__summary-text');
        const reviewCount = this.getReviewCount(summaryText);
        if (summaryText && reviewCount > 0 && !/based on/i.test(summaryText.textContent)) {
          summaryText.textContent = `Based on ${reviewCount} reviews`;
        }
      }

      enhanceSortDropdown() {
        const sortWrapper = this.querySelector('.jm-sort-filter, .jdgm-sort-dropdown-wrapper');
        const sortSelect = sortWrapper?.querySelector('.jdgm-sort-dropdown, .jm-sort-filter__select');
        if (!sortWrapper || !sortSelect) return;

        sortWrapper.classList.add('theme-judgeme-sort');
        if (!sortSelect.getAttribute('aria-label')) {
          sortSelect.setAttribute('aria-label', 'Sort reviews');
        }
      }

      enhanceFilterDropdown() {
        const sourceSelect = this.getSourceFilterSelect();
        if (!sourceSelect || sourceSelect.options.length < 2) return;

        let filter = this.querySelector('.theme-judgeme-review-filter');
        if (!filter) {
          const productId =
            this.querySelector('.jm-review-widget, .jdgm-review-widget')?.dataset.productId ||
            this.querySelector('.jdgm-review-widget')?.dataset.id ||
            'product';
          const selectId = `ThemeReviewFilter-${productId}`;

          filter = document.createElement('div');
          filter.className = 'theme-judgeme-review-filter';
          filter.innerHTML = `
            <label class="visually-hidden" for="${selectId}">Filter reviews</label>
            <select id="${selectId}" class="theme-judgeme-review-filter__select" aria-label="Filter reviews"></select>
          `;

          const filterSelect = filter.querySelector('select');
          filterSelect.addEventListener('pointerdown', () => {
            this.syncFilterOptions(filterSelect, this.getSourceFilterSelect());
          });
          filterSelect.addEventListener('focus', () => {
            this.syncFilterOptions(filterSelect, this.getSourceFilterSelect());
          });
          filterSelect.addEventListener('change', (event) => {
            const currentSource = this.getSourceFilterSelect();
            if (!currentSource) return;

            this.setNativeSelectValue(currentSource, event.currentTarget.value);
            currentSource.dispatchEvent(new Event('input', { bubbles: true }));
            currentSource.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }

        const filterSelect = filter.querySelector('select');
        this.syncFilterOptions(filterSelect, sourceSelect);

        const gallery = this.querySelector('.theme-judgeme-media-gallery--custom');
        const list = this.getReviewList();
        if (gallery?.parentElement) {
          if (gallery.nextElementSibling !== filter) {
            gallery.insertAdjacentElement('afterend', filter);
          }
        } else if (list?.parentElement && list.previousElementSibling !== filter) {
          list.parentElement.insertBefore(filter, list);
        }

        this.classList.add('has-theme-review-filter');
      }

      getSourceFilterSelect() {
        return this.querySelector(
          '.jm-review-widget-header__filters-wrapper .jm-sort-filter__select, .jm-review-widget-header__filters-wrapper .jdgm-sort-dropdown, .jdgm-row-actions .jdgm-sort-dropdown, .jm-sort-filter__select, .jdgm-sort-dropdown'
        );
      }

      syncFilterOptions(filterSelect, sourceSelect) {
        if (!filterSelect || !sourceSelect) return;

        const options = Array.from(sourceSelect.options);
        const optionSignature = options
          .map(
            (option) =>
              `${option.value}:${option.textContent.trim()}:${option.disabled ? 'disabled' : 'enabled'}`
          )
          .join('|');

        if (filterSelect.dataset.optionSignature !== optionSignature) {
          filterSelect.replaceChildren(
            ...options.map((option) => {
              const clone = document.createElement('option');
              clone.value = option.value;
              clone.textContent = option.textContent.trim();
              clone.disabled = option.disabled;
              return clone;
            })
          );
          filterSelect.dataset.optionSignature = optionSignature;
        }

        filterSelect.disabled = sourceSelect.disabled;
        filterSelect.value = sourceSelect.value;
      }

      setNativeSelectValue(select, value) {
        const descriptor = Object.getOwnPropertyDescriptor(
          window.HTMLSelectElement.prototype,
          'value'
        );
        descriptor?.set?.call(select, value);
      }

      enhanceMediaFields() {
        document
          .querySelectorAll(
            '.jdgm-form-wrapper input[type="file"], .jdgm-write-review-modal input[type="file"], .jdgm-modal input[type="file"], .jdgm-modal-block input[type="file"]'
          )
          .forEach((input) => {
            input.accept =
              'image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/x-m4v,video/*';
            input.multiple = true;
          });
      }

      enhanceNativeReviewFormStyle() {
        document
          .querySelectorAll('.jdgm-write-review-modal, .jdgm-modal, .jdgm-modal-block')
          .forEach((candidate) => {
            if (
              !candidate.querySelector(
                '.jdgm-write-review-modal__field-group, .jdgm-form, input, textarea'
              )
            ) {
              return;
            }

            const modal =
              candidate.closest('.jdgm-modal, .jdgm-modal-block') || candidate;
            modal.classList.add('theme-judgeme-review-modal');

            candidate
              .querySelectorAll(
                '.jdgm-write-review-modal__field-group, .jdgm-form__fieldset'
              )
              .forEach((fieldGroup) => {
                const input = fieldGroup.querySelector('input, textarea, select');
                if (!input) return;

                if (input.type === 'file') {
                  fieldGroup.classList.add('theme-judgeme-media-field');
                }
              });

            this.protectReviewTitleFromContent(candidate);
          });
      }

      protectReviewTitleFromContent(container) {
        const titleInput = this.getReviewFieldInput(container, 'title');
        const contentInput = this.getReviewFieldInput(container, 'content');
        if (!titleInput || !contentInput) return;

        const state =
          container.__themeReviewFieldState ||
          (container.__themeReviewFieldState = {
            titleInput: null,
            contentInput: null,
            titleValue: '',
            restoreFrame: 0,
            restoringTitle: false,
          });

        titleInput.classList.add('theme-judgeme-review-title-input');
        contentInput.classList.add('theme-judgeme-review-content-input');

        if (state.titleInput !== titleInput) {
          state.titleInput = titleInput;
          state.titleValue = titleInput.value;
          titleInput.addEventListener('input', () => {
            if (!state.restoringTitle) state.titleValue = titleInput.value;
          });
          titleInput.addEventListener('change', () => {
            if (!state.restoringTitle) state.titleValue = titleInput.value;
          });
        }

        if (state.contentInput !== contentInput) {
          state.contentInput = contentInput;
          const scheduleRestore = () => {
            cancelAnimationFrame(state.restoreFrame);
            let attempts = 0;
            const restore = () => {
              this.restoreReviewTitleValue(state);
              attempts += 1;
              if (attempts < 3) state.restoreFrame = requestAnimationFrame(restore);
            };
            state.restoreFrame = requestAnimationFrame(restore);
          };
          contentInput.addEventListener('input', scheduleRestore);
          contentInput.addEventListener('change', scheduleRestore);
        }
      }

      getReviewFieldInput(container, fieldType) {
        const selectors =
          fieldType === 'title'
            ? [
                'input[name*="title" i]',
                'input[id*="title" i]',
                'input[data-testid*="title" i]',
                '.jdgm-write-review-modal__field-group--title input',
                '.jdgm-form__fieldset--title input',
              ]
            : [
                'textarea[name*="body" i]',
                'textarea[name*="content" i]',
                'textarea[id*="body" i]',
                'textarea[id*="content" i]',
                'textarea[data-testid*="body" i]',
                'textarea[data-testid*="content" i]',
                '.jdgm-write-review-modal__field-group--body textarea',
                '.jdgm-write-review-modal__field-group--content textarea',
                '.jdgm-form__fieldset--body textarea',
              ];

        const directMatch = container.querySelector(selectors.join(', '));
        if (directMatch) return directMatch;

        const fieldGroups = Array.from(
          container.querySelectorAll(
            '.jdgm-write-review-modal__field-group, .jdgm-form__fieldset, .jdgm-form__field'
          )
        );
        const labelPattern = fieldType === 'title' ? /review\s*title|title/i : /review\s*content|body|content/i;
        return fieldGroups.find((fieldGroup) => {
          const label = fieldGroup.querySelector('label, legend, .jdgm-write-review-modal__field-label');
          return labelPattern.test(label?.textContent?.trim() || '');
        })?.querySelector(fieldType === 'title' ? 'input' : 'textarea');
      }

      restoreReviewTitleValue(state) {
        const titleInput = state.titleInput;
        if (!titleInput?.isConnected || state.restoringTitle) return;

        const expectedValue = state.titleValue || '';
        if (titleInput.value === expectedValue) return;

        state.restoringTitle = true;
        try {
          const descriptor = Object.getOwnPropertyDescriptor(
            Object.getPrototypeOf(titleInput),
            'value'
          );
          descriptor?.set?.call(titleInput, expectedValue);
          titleInput.dispatchEvent(new Event('input', { bubbles: true }));
          titleInput.dispatchEvent(new Event('change', { bubbles: true }));
        } finally {
          state.restoringTitle = false;
        }
      }

      enhanceMediaGallery() {
        const media = this.collectReviewMedia();
        const gallery = this.getOrCreateMediaGallery(media.length > 0);

        this.querySelectorAll('.jm-media-preview').forEach((nativeGallery) => {
          nativeGallery.classList.add('theme-judgeme-native-media-gallery-hidden');
          nativeGallery.setAttribute('aria-hidden', 'true');
        });

        if (!media.length || !gallery) return;

        const signature = media
          .map((item) => `${item.reviewKey}:${item.type}:${item.source}`)
          .join('|');
        if (gallery.dataset.mediaSignature === signature) return;
        gallery.dataset.mediaSignature = signature;

        const items = gallery.querySelector('.theme-judgeme-media-gallery__items');
        items.replaceChildren();
        media.forEach((item, index) => {
          const trigger = document.createElement('button');
          trigger.type = 'button';
          trigger.className = 'theme-judgeme-media-gallery__item';
          trigger.setAttribute('role', 'listitem');
          trigger.setAttribute(
            'aria-label',
            `View customer ${item.type === 'video' ? 'video' : 'photo'} ${index + 1}`
          );

          const image = document.createElement('img');
          image.src = item.thumbnail || item.source;
          image.alt = '';
          image.loading = 'lazy';
          trigger.append(image);

          if (item.type === 'video') {
            const play = document.createElement('span');
            play.className = 'theme-judgeme-media-gallery__play';
            play.setAttribute('aria-hidden', 'true');
            trigger.append(play);
          }

          trigger.addEventListener('click', () => {
            const activeIndex = Math.max(0, media.indexOf(item));
            this.openMediaViewer(media, activeIndex, true, trigger);
          });
          items.append(trigger);
        });
      }

      collectReviewMedia() {
        const media = [];
        const seen = new Set();
        this.mediaByTrigger = new WeakMap();

        this.querySelectorAll('.jm-review-item, .jdgm-rev').forEach((review, reviewIndex) => {
          const reviewKey =
            review.dataset.reviewId ||
            review.dataset.id ||
            review.id ||
            `review-${reviewIndex}`;
          const candidates = Array.from(
            review.querySelectorAll(
              '[aria-label="View review image"], [aria-label="Play review video"], .jm-media-thumbnail-list__thumbnail, .jdgm-rev__pic-link, .jdgm-rev__video-link, .jdgm-rev__video'
            )
          ).filter(
            (candidate, index, allCandidates) =>
              !allCandidates.some(
                (other, otherIndex) =>
                  otherIndex !== index && other.contains(candidate)
              )
          );

          candidates.forEach((trigger) => {
            const item = this.createMediaItem(trigger, review, reviewKey);
            if (!item) return;

            const uniqueKey = `${reviewKey}:${item.type}:${item.source}`;
            if (seen.has(uniqueKey)) return;
            seen.add(uniqueKey);
            media.push(item);
            this.mediaByTrigger.set(trigger, item);
          });

          this.mergeReviewDataMedia(
            media,
            seen,
            review,
            reviewKey,
            this.getReviewData(reviewKey, reviewIndex)
          );
        });

        return media;
      }

      getReviewData(reviewKey, reviewIndex) {
        const widget = this.querySelector('.jdgm-review-widget');
        const productId = widget?.dataset.productId || widget?.dataset.id;
        const widgetData = productId ? window.jdgm?.data?.reviewWidget?.[productId] : null;
        const reviewData = Array.isArray(widgetData)
          ? widgetData
          : Array.isArray(widgetData?.reviews)
            ? widgetData.reviews
            : null;
        if (!Array.isArray(reviewData)) return null;

        return (
          reviewData.find(
            (candidate) =>
              String(candidate.id || candidate.review_id || '') === String(reviewKey)
          ) || reviewData[reviewIndex] || null
        );
      }

      getReviewDataMediaSources(reviewData) {
        if (!reviewData) return [];

        const media = [
          reviewData.pictures_urls,
          reviewData.images,
          reviewData.videos_urls,
          reviewData.videos,
        ].flatMap((value) => (Array.isArray(value) ? value : value ? [value] : []));

        return media
          .map((item) => {
            if (typeof item === 'string') return item;
            return item?.url || item?.src || item?.image_url || item?.video_url || '';
          })
          .filter((source) => this.isMediaSource(source));
      }

      mergeReviewDataMedia(media, seen, review, reviewKey, reviewData) {
        const sources = this.getReviewDataMediaSources(reviewData);
        if (!sources.length) return;

        const existingReviewMedia = media.filter((item) => item.reviewKey === reviewKey);
        sources.forEach((source, mediaIndex) => {
          const normalizedSource = this.normalizeMediaUrl(source);
          let existing = existingReviewMedia.find(
            (item) =>
              item.source === normalizedSource ||
              (item.thumbnail &&
                this.normalizeMediaUrl(item.thumbnail) === normalizedSource)
          );

          if (
            !existing &&
            existingReviewMedia.length === sources.length &&
            existingReviewMedia[mediaIndex]
          ) {
            existing = existingReviewMedia[mediaIndex];
          }

          if (existing) {
            existing.source = normalizedSource;
            return;
          }

          const type = /video/i.test(source) ? 'video' : 'image';
          const uniqueKey = `${reviewKey}:${type}:${normalizedSource}`;
          if (seen.has(uniqueKey)) return;
          seen.add(uniqueKey);
          media.push({
            trigger: null,
            review,
            reviewKey,
            source: normalizedSource,
            thumbnail: normalizedSource,
            type,
          });
        });
      }

      createMediaItem(trigger, review, reviewKey) {
        const video = trigger.matches('video') ? trigger : trigger.querySelector('video');
        const image = trigger.matches('img') ? trigger : trigger.querySelector('img');
        const link = trigger.closest('a');
        const type =
          video ||
          /video/i.test(
            `${trigger.getAttribute('aria-label') || ''} ${trigger.className || ''}`
          )
            ? 'video'
            : 'image';

        const sourceCandidates = [
          trigger.dataset.fullSrc,
          trigger.dataset.originalSrc,
          trigger.dataset.original,
          trigger.dataset.fullUrl,
          trigger.dataset.originalUrl,
          ...this.getMediaDataSources(trigger),
          trigger.dataset.src,
          trigger.dataset.url,
          link?.dataset.fullSrc,
          link?.dataset.originalSrc,
          link?.dataset.original,
          link?.dataset.fullUrl,
          link?.dataset.originalUrl,
          ...this.getMediaDataSources(link),
          type === 'image' ? this.getMediaLinkSource(link) : '',
          video?.currentSrc,
          video?.src,
          video?.dataset.fullSrc,
          video?.dataset.originalSrc,
          ...this.getMediaDataSources(video),
          image?.dataset.fullSrc,
          image?.dataset.originalSrc,
          image?.dataset.original,
          image?.dataset.fullUrl,
          image?.dataset.originalUrl,
          ...this.getMediaDataSources(image),
          this.getLargestSrcsetUrl(image?.getAttribute('srcset')),
          image?.currentSrc,
          image?.src,
          video?.poster,
        ];
        let source = sourceCandidates.find(
          (candidate) => candidate && !/^javascript:/i.test(candidate)
        );
        const thumbnail = image?.currentSrc || image?.src || video?.poster || source;

        if (!source) return null;
        source = this.normalizeMediaUrl(source);

        return {
          trigger,
          review,
          reviewKey,
          source,
          thumbnail,
          type,
        };
      }

      getMediaDataSources(element) {
        if (!element?.dataset) return [];

        return Object.entries(element.dataset)
          .filter(
            ([key, value]) =>
              key !== 'srcset' && /(full|original|media|image|source|url|src)/i.test(key)
          )
          .map(([, value]) => value)
          .filter((value) => this.isMediaSource(value) && !/[\s,]/.test(value));
      }

      getMediaLinkSource(link) {
        const href = link?.href || link?.getAttribute('href');
        if (!this.isMediaSource(href)) return '';

        try {
          const url = new URL(href, window.location.href);
          if (url.pathname === window.location.pathname && url.origin === window.location.origin) {
            return '';
          }
        } catch (error) {
          return '';
        }

        return href;
      }

      isMediaSource(source) {
        return Boolean(
          source &&
            !/^javascript:|^#|^mailto:|^tel:/i.test(source) &&
            /^(?:https?:|data:image\/|blob:|\/|\/\/)/i.test(source)
        );
      }

      getLargestSrcsetUrl(srcset) {
        if (!srcset) return '';

        return srcset
          .split(',')
          .map((candidate) => {
            const [url, descriptor] = candidate.trim().split(/\s+/);
            const width = Number.parseInt(descriptor?.replace('w', ''), 10);
            return { url, width: Number.isFinite(width) ? width : 0 };
          })
          .sort((left, right) => right.width - left.width)[0]?.url;
      }

      normalizeMediaUrl(source) {
        try {
          const url = new URL(source, window.location.href);
          ['width', 'height', 'crop'].forEach((parameter) => url.searchParams.delete(parameter));
          url.pathname = url.pathname.replace(
            /_(?:\d+x\d+|pico|icon|thumb|thumbnail|small|compact|medium|large|grande|master)(?=\.[a-z0-9]+$)/i,
            ''
          );
          return url.href;
        } catch (error) {
          return source;
        }
      }

      getOrCreateMediaGallery(hasMedia) {
        let gallery = this.querySelector('.theme-judgeme-media-gallery--custom');
        if (!hasMedia) {
          gallery?.remove();
          return null;
        }

        if (!gallery) {
          gallery = document.createElement('section');
          gallery.className =
            'theme-judgeme-media-gallery theme-judgeme-media-gallery--custom';
          gallery.innerHTML = `
            <h3 class="theme-judgeme-media-gallery__title">Customer photos & videos</h3>
            <div class="theme-judgeme-media-gallery__items" role="list"></div>
          `;

          const list = this.getReviewList();
          const filter = this.querySelector('.theme-judgeme-review-filter');
          const anchor = filter || list;
          anchor?.parentElement?.insertBefore(gallery, anchor);
        }

        return gallery;
      }

      getReviewList() {
        return (
          this.querySelector(
            '.jm-review-widget__body, .jm-review-widget-list, .jm-review-list, .jdgm-rev-widg__body, .jdgm-rev-widg__reviews, .jdgm-review-list'
          ) || this.querySelector('.jm-review-item, .jdgm-rev')?.parentElement
        );
      }

      onClick(event) {
        const trigger = event.target.closest(
          '[aria-label="View review image"], [aria-label="Play review video"], .jm-media-thumbnail-list__thumbnail, .jdgm-rev__pic-link, .jdgm-rev__video-link, .jdgm-rev__video'
        );
        if (!trigger || trigger.closest('.theme-judgeme-media-gallery--custom')) return;

        const item =
          this.mediaByTrigger.get(trigger) ||
          this.createMediaItem(
            trigger,
            trigger.closest('.jm-review-item, .jdgm-rev'),
            trigger.closest('.jm-review-item, .jdgm-rev')?.dataset.reviewId || 'review'
          );
        if (!item) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const allMedia = this.collectReviewMedia();
        const activeIndex = Math.max(
          0,
          allMedia.findIndex(
            (candidate) =>
              candidate.reviewKey === item.reviewKey && candidate.source === item.source
          )
        );
        this.openMediaViewer(allMedia.length ? allMedia : [item], activeIndex, false, trigger);
      }

      openMediaViewer(media, activeIndex, showReview, trigger) {
        if (!media.length) return;

        const viewer = this.getOrCreateMediaViewer();
        viewer.media = media;
        viewer.activeIndex = activeIndex;
        viewer.showReview = showReview;
        viewer.returnFocus = trigger;
        viewer.classList.toggle('is-review-detail', showReview);
        viewer.classList.toggle('is-media-only', !showReview);
        viewer.hidden = false;
        viewer.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('theme-judgeme-media-viewer-open');
        this.renderMediaViewer(viewer);
        viewer.querySelector('.theme-judgeme-media-viewer__close')?.focus({
          preventScroll: true,
        });
      }

      getOrCreateMediaViewer() {
        if (this.mediaViewer?.isConnected) return this.mediaViewer;

        const viewer = document.createElement('div');
        viewer.className = 'theme-judgeme-media-viewer';
        viewer.hidden = true;
        viewer.setAttribute('aria-hidden', 'true');
        viewer.setAttribute('role', 'dialog');
        viewer.setAttribute('aria-modal', 'true');
        viewer.setAttribute('aria-label', 'Customer review media');
        viewer.innerHTML = `
          <div class="theme-judgeme-media-viewer__backdrop" data-theme-media-close></div>
          <div class="theme-judgeme-media-viewer__dialog">
            <button
              type="button"
              class="theme-judgeme-media-viewer__close"
              data-theme-media-close
              aria-label="Close media viewer"
            ></button>
            <div class="theme-judgeme-media-viewer__layout">
              <section class="theme-judgeme-media-viewer__stage">
                <div class="theme-judgeme-media-viewer__media"></div>
                <button
                  type="button"
                  class="theme-judgeme-media-viewer__previous"
                  data-theme-media-previous
                  aria-label="Previous media"
                ></button>
                <button
                  type="button"
                  class="theme-judgeme-media-viewer__next"
                  data-theme-media-next
                  aria-label="Next media"
                ></button>
                <div class="theme-judgeme-media-viewer__thumbnails" role="list"></div>
              </section>
              <aside class="theme-judgeme-media-viewer__review"></aside>
            </div>
          </div>
        `;

        viewer.addEventListener('click', (event) => {
          if (event.target.closest('[data-theme-media-close]')) {
            this.closeMediaViewer();
            return;
          }

          if (event.target.closest('[data-theme-media-previous]')) {
            this.changeMediaViewerItem(-1);
            return;
          }

          if (event.target.closest('[data-theme-media-next]')) {
            this.changeMediaViewerItem(1);
            return;
          }

          const thumbnail = event.target.closest('[data-theme-media-index]');
          if (thumbnail) {
            viewer.activeIndex = Number.parseInt(thumbnail.dataset.themeMediaIndex, 10) || 0;
            this.renderMediaViewer(viewer);
          }
        });
        viewer.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeMediaViewer();
          if (event.key === 'ArrowLeft') this.changeMediaViewerItem(-1);
          if (event.key === 'ArrowRight') this.changeMediaViewerItem(1);
        });

        document.body.append(viewer);
        this.mediaViewer = viewer;
        return viewer;
      }

      renderMediaViewer(viewer) {
        const item = viewer.media[viewer.activeIndex] || viewer.media[0];
        const mediaContainer = viewer.querySelector('.theme-judgeme-media-viewer__media');
        const thumbnails = viewer.querySelector('.theme-judgeme-media-viewer__thumbnails');
        const review = viewer.querySelector('.theme-judgeme-media-viewer__review');
        const previous = viewer.querySelector('.theme-judgeme-media-viewer__previous');
        const next = viewer.querySelector('.theme-judgeme-media-viewer__next');

        mediaContainer.replaceChildren(this.createViewerMedia(item));
        thumbnails.replaceChildren();
        viewer.media.forEach((mediaItem, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'theme-judgeme-media-viewer__thumbnail';
          button.dataset.themeMediaIndex = String(index);
          button.setAttribute('role', 'listitem');
          button.setAttribute(
            'aria-label',
            `View ${mediaItem.type === 'video' ? 'video' : 'photo'} ${index + 1}`
          );
          button.classList.toggle('is-active', index === viewer.activeIndex);

          const image = document.createElement('img');
          image.src = mediaItem.thumbnail || mediaItem.source;
          image.alt = '';
          button.append(image);
          thumbnails.append(button);
        });

        const hasMultipleMedia = viewer.media.length > 1;
        previous.hidden = !hasMultipleMedia;
        next.hidden = !hasMultipleMedia;
        thumbnails.hidden = !hasMultipleMedia;

        if (viewer.showReview) {
          review.replaceChildren(this.createReviewDetail(item.review));
        } else {
          review.replaceChildren();
        }
      }

      createViewerMedia(item) {
        if (item.type === 'video') {
          const video = document.createElement('video');
          video.src = item.source;
          video.controls = true;
          video.preload = 'metadata';
          video.setAttribute('playsinline', '');
          video.style.setProperty('width', '100%', 'important');
          video.style.setProperty('height', '100%', 'important');
          video.style.setProperty('max-width', '100%', 'important');
          video.style.setProperty('max-height', '100%', 'important');
          video.style.setProperty('object-fit', 'contain', 'important');
          video.style.objectPosition = 'center';
          return video;
        }

        const image = document.createElement('img');
        image.src = item.source;
        image.alt = 'Customer review photo';
        image.decoding = 'async';
        image.style.setProperty('width', '100%', 'important');
        image.style.setProperty('height', '100%', 'important');
        image.style.setProperty('max-width', '100%', 'important');
        image.style.setProperty('max-height', '100%', 'important');
        image.style.setProperty('object-fit', 'contain', 'important');
        image.style.objectPosition = 'center';
        image.addEventListener(
          'error',
          () => {
            if (item.thumbnail && image.src !== item.thumbnail) image.src = item.thumbnail;
          },
          { once: true }
        );
        return image;
      }

      createReviewDetail(review) {
        const content = document.createElement('div');
        content.className = 'theme-judgeme-media-viewer__review-content';
        if (!review) return content;

        const rating = review.querySelector('.jm-star-rating, .jdgm-rev__rating')?.cloneNode(true);
        const reviewerText = this.getFirstText(review, [
          '.jm-reviewer-info__name',
          '.jm-review-item__reviewer-name',
          '.jdgm-rev__author',
          '.jdgm-rev__author-wrapper .jdgm-rev__author',
          '.jdgm-rev__author-wrapper',
        ]);
        const titleText = this.getFirstText(review, [
          '.jm-review-content__title',
          '.jm-review-item__title',
          '.jdgm-rev__title',
        ]);
        const bodySource = review.querySelector(
          '.jm-review-content__body, .jm-review-item__content, .jdgm-rev__body, .jdgm-rev__content'
        );
        const verified = review
          .querySelector(
            '.jm-reviewer-info__verified-buyer, .jdgm-rev__buyer-badge, [data-testid="verified-buyer"]'
          )
          ?.cloneNode(true);

        const meta = document.createElement('div');
        meta.className = 'theme-judgeme-media-viewer__review-rating';
        if (rating) meta.append(rating);
        if (meta.childNodes.length) content.append(meta);

        if (reviewerText || verified) {
          const reviewer = document.createElement('div');
          reviewer.className = 'theme-judgeme-media-viewer__reviewer';
          if (reviewerText) {
            const name = document.createElement('span');
            name.textContent = reviewerText;
            reviewer.append(name);
          }
          if (verified) reviewer.append(verified);
          content.append(reviewer);
        }

        if (titleText) {
          const title = document.createElement('h3');
          title.className = 'theme-judgeme-media-viewer__review-title';
          title.textContent = titleText;
          content.append(title);
        }

        if (bodySource) {
          const body = bodySource.cloneNode(true);
          body.classList.add('theme-judgeme-media-viewer__review-body');
          body
            .querySelectorAll(
              '.jm-star-rating, .jm-reviewer-info, .jm-review-content__title, .jm-review-item__title, .jdgm-rev__rating, .jdgm-rev__author, .jdgm-rev__author-wrapper, .jdgm-rev__title, .jm-media-thumbnail-list, .jm-review-media, .jdgm-rev__pics, .jdgm-rev__videos, button, script'
            )
            .forEach((element) => element.remove());
          content.append(body);
        }

        return content;
      }

      getFirstText(container, selectors) {
        for (const selector of selectors) {
          const text = container.querySelector(selector)?.textContent?.trim();
          if (text) return text;
        }
        return '';
      }

      changeMediaViewerItem(direction) {
        const viewer = this.mediaViewer;
        if (!viewer || viewer.hidden || viewer.media.length < 2) return;

        viewer.activeIndex =
          (viewer.activeIndex + direction + viewer.media.length) % viewer.media.length;
        this.renderMediaViewer(viewer);
      }

      closeMediaViewer() {
        const viewer = this.mediaViewer;
        if (!viewer || viewer.hidden) return;

        viewer.querySelector('video')?.pause();
        viewer.hidden = true;
        viewer.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('theme-judgeme-media-viewer-open');
        viewer.returnFocus?.focus?.({ preventScroll: true });
      }

      getAverageRating(summary = this) {
        const configuredRating = Number.parseFloat(this.dataset.averageRating);
        if (configuredRating > 0) return configuredRating;

        const scoredElement = summary.querySelector('[data-score]');
        const elementRating = Number.parseFloat(scoredElement?.dataset.score);
        if (elementRating > 0) return elementRating;

        const label = summary.getAttribute('aria-label') || summary.textContent;
        const labelRating = Number.parseFloat(label?.match(/\d+(?:\.\d+)?/)?.[0]);
        return labelRating > 0 ? labelRating : 0;
      }

      getReviewCount(summaryText) {
        const configuredCount = Number.parseInt(this.dataset.numberOfReviews, 10);
        if (configuredCount > 0) return configuredCount;

        const textCount = Number.parseInt(
          summaryText?.textContent?.match(/\d[\d,]*/)?.[0]?.replaceAll(',', ''),
          10
        );
        return textCount > 0 ? textCount : 0;
      }

      formatRating(rating) {
        return rating.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
      }

      setText(element, value) {
        if (element && element.textContent !== value) element.textContent = value;
      }
    }
  );
}
